import { gql } from './graphql-client.js';
import { emit } from './event-bus.js';
import type { AuthResult, Person } from '../types.js';

interface AuthState {
  token: string | null;
  personId: number | null;
  displayName: string | null;
  isAdmin: boolean;
  initialized: boolean;
}

interface ImpersonateState {
  personId: number;
  displayName: string;
}

class AuthStore {
  private state: AuthState;
  private impersonating: ImpersonateState | null = null;
  private listeners = new Set<() => void>();

  constructor() {
    this.state = {
      token: localStorage.getItem('session_token'),
      personId: parseInt(localStorage.getItem('person_id') ?? '') || null,
      displayName: localStorage.getItem('display_name'),
      isAdmin: localStorage.getItem('is_admin') === 'true',
      initialized: false,
    };
    gql.setToken(this.state.token);
  }

  get isLoggedIn(): boolean { return this.state.token !== null && this.state.personId !== null; }
  get isAdmin(): boolean { return this.state.isAdmin; }
  get displayName(): string | null { return this.state.displayName; }
  get personId(): number | null { return this.state.personId; }
  get initialized(): boolean { return this.state.initialized; }

  /** The effective person ID — impersonated person or the real logged-in user */
  get effectivePersonId(): number | null {
    return this.impersonating?.personId ?? this.state.personId;
  }

  get effectiveDisplayName(): string | null {
    return this.impersonating?.displayName ?? this.state.displayName;
  }

  get isImpersonating(): boolean {
    return this.impersonating !== null;
  }

  get impersonatedName(): string | null {
    return this.impersonating?.displayName ?? null;
  }

  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private notify(): void {
    this.listeners.forEach(cb => cb());
    emit('auth-changed');
  }

  login(result: AuthResult): void {
    this.state.token = result.sessionToken;
    this.state.personId = result.personId;
    this.state.displayName = result.displayName;
    this.state.isAdmin = result.isAdmin;

    localStorage.setItem('session_token', result.sessionToken);
    localStorage.setItem('person_id', String(result.personId));
    localStorage.setItem('display_name', result.displayName);
    localStorage.setItem('is_admin', String(result.isAdmin));

    gql.setToken(result.sessionToken);
    this.notify();
  }

  logout(): void {
    // Fire-and-forget server logout
    gql.query('mutation { logout }').catch(() => {});

    this.state.token = null;
    this.state.personId = null;
    this.state.displayName = null;
    this.state.isAdmin = false;
    this.impersonating = null;

    localStorage.removeItem('session_token');
    localStorage.removeItem('person_id');
    localStorage.removeItem('display_name');
    localStorage.removeItem('is_admin');

    gql.setToken(null);
    this.notify();
  }

  /** Admin impersonates another user */
  impersonate(personId: number, displayName: string): void {
    if (!this.state.isAdmin) return;
    this.impersonating = { personId, displayName };
    this.notify();
  }

  /** Stop impersonating — return to admin view */
  stopImpersonating(): void {
    this.impersonating = null;
    this.notify();
  }

  /**
   * Fetch the effective person's data — impersonated person or self.
   * Single source of truth for all user-facing pages.
   */
  async fetchCurrentPerson(fields: string): Promise<Person | null> {
    if (this.impersonating) {
      const data = await gql.query<{ person: Person | null }>(
        `query($id: Int!) { person(id: $id) { ${fields} } }`,
        { id: this.impersonating.personId },
      );
      return data.person;
    }
    const data = await gql.query<{ me: Person | null }>(
      `query { me { ${fields} } }`,
    );
    return data.me;
  }

  async initialize(): Promise<void> {
    if (!this.state.token) {
      this.state.initialized = true;
      this.notify();
      return;
    }

    try {
      const data = await gql.query<{ me: Person | null }>(
        'query { me { id firstName lastName displayName email phone isActive isAdmin } }'
      );
      if (data.me) {
        this.state.personId = data.me.id;
        this.state.displayName = data.me.displayName;
        this.state.isAdmin = data.me.isAdmin;
        localStorage.setItem('person_id', String(data.me.id));
        localStorage.setItem('display_name', data.me.displayName);
        localStorage.setItem('is_admin', String(data.me.isAdmin));
      } else {
        // Session expired
        this.logout();
      }
    } catch {
      this.logout();
    }

    this.state.initialized = true;
    this.notify();
  }
}

export const auth = new AuthStore();
